/**
 * Unify PractitionerRole accessors.
 *
 * Assignment provenance for Settings → Permissions is stored on the role
 * resource (not `meta.author`) because admin-actions / role-assignment bots
 * write with `runAsUser: false`, so version history authors the Bot.
 */
import type { PractitionerRole, Reference } from '@medplum/fhirtypes';
import { EXT_URLS, PRACTITIONER_ROLE_SYSTEM } from '../constants';

const PRACTITIONER_ROLE_CODES = new Set([
  'provider',
  'supervisor',
  'practice-owner',
  'practice-admin',
  'practice-assistant',
  'unify-admin',
  'unify-assistant',
]);

export function getRoleAssignedBy(pr: PractitionerRole): Reference | undefined {
  return pr.extension?.find((e) => e.url === EXT_URLS.roleAssignedBy)?.valueReference;
}

export function setRoleAssignedBy(pr: PractitionerRole, assignedBy: Reference): PractitionerRole {
  const extension = (pr.extension ?? []).filter((e) => e.url !== EXT_URLS.roleAssignedBy);
  extension.push({ url: EXT_URLS.roleAssignedBy, valueReference: assignedBy });
  return { ...pr, extension };
}

/** Functional role slugs from `PractitionerRole.code[]` (Unify practitioner-role CodeSystem). */
export function getPractitionerRoleCodes(pr: PractitionerRole | undefined): string[] {
  if (!pr?.code?.length) return [];
  const slugs: string[] = [];
  for (const concept of pr.code) {
    for (const coding of concept.coding ?? []) {
      const code = coding.code?.trim();
      if (!code || slugs.includes(code)) continue;
      if (coding.system && coding.system !== PRACTITIONER_ROLE_SYSTEM) continue;
      if (!coding.system && !PRACTITIONER_ROLE_CODES.has(code)) continue;
      slugs.push(code);
    }
  }
  return slugs;
}
